// Media-Matchers lead API layer, shared by the request-information flow and
// the /program-information templates.
//
// Five GET endpoints make up one lead:
//   TCPA Search   — returns the consent disclosure + mRecordID (before submit)
//   TCPA Post     — records the consent signature (on submit)
//   Leads Search  — returns partner schools to present one at a time
//   Leads Post    — one per school the user consents to
//   Clicks Search — returns click-out offers for the second carousel
//
// Search calls fall back to JSONP because CORS blocks the response in some
// browsers; Post calls fire three ways at once because the visitor navigates
// away immediately after submitting.
import { getPublisherParams } from './publisherParams'
import { applyUrlPassthrough, pruneEmptyParams } from './urlPassthrough'
import { getWebLandingURL } from './landingUrl'
import { getMetaCookies, resolveSub3 } from './metaCookies'
import { readCertificationFields } from './leadCertificationScripts'
import { toStateAbbr } from './usStates'

// ---------------------------------------------------------------------------
// Value mappings (partner spec)
// ---------------------------------------------------------------------------

// The spec only defines 1, 2, 5, 7, and 9, which is why several form options
// collapse onto the same code.
export function educationToApiValue(formValue: string): number {
  const map: Record<string, number> = {
    'some-high-school': 2,
    'high-school': 1,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7,
  }
  return map[formValue] || 9
}

export function startTimeframeToApiValue(formValue: string): number {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6,
  }
  return map[formValue] || 6
}

// None=1, Active Duty=2, Veteran=3, National Guard=4, Spouse=5, Dependent=6.
// The spec has no code for "Unknown", so it is sent blank.
export function militaryAffiliationToApiValue(formValue: string): string {
  const map: Record<string, string> = {
    none: '1',
    'active-duty': '2',
    veteran: '3',
    guard: '4',
    spouse: '5',
    dependent: '6',
  }
  return map[formValue] || ''
}

export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// The disclosure comes back as partner-authored HTML that can contain links to
// their privacy policy and terms. Only inline formatting and anchors survive,
// and every anchor is forced to open safely in a new tab.
export function sanitizeTcpaHtml(raw: string, linkClass = 'underline'): string {
  if (!raw) return ''
  if (typeof document === 'undefined') return raw
  const ALLOWED_TAGS = new Set(['A', 'BR', 'STRONG', 'B', 'EM', 'I', 'SPAN', 'P'])
  const container = document.createElement('div')
  container.innerHTML = raw
  const walk = (node: Element) => {
    // Iterate a static copy — the tree is mutated during traversal.
    Array.from(node.children).forEach((child) => {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        child.replaceWith(document.createTextNode(child.textContent || ''))
        return
      }
      Array.from(child.attributes).forEach((attr) => {
        if (child.tagName === 'A' && attr.name === 'href') return
        child.removeAttribute(attr.name)
      })
      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || ''
        if (/^\s*(javascript|data|vbscript):/i.test(href)) {
          child.removeAttribute('href')
        }
        child.setAttribute('target', '_blank')
        child.setAttribute('rel', 'noopener noreferrer')
        child.setAttribute('class', linkClass)
      }
      walk(child)
    })
  }
  walk(container)
  return container.innerHTML
}

// ---------------------------------------------------------------------------
// Request plumbing
// ---------------------------------------------------------------------------

function jsonpRequest<T>(url: string, timeoutMs = 8000): Promise<T> {
  return new Promise((resolve, reject) => {
    const callbackName = `mmcb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`
    const script = document.createElement('script')
    let settled = false
    const cleanup = () => {
      try {
        delete (window as any)[callbackName]
      } catch {
        ;(window as any)[callbackName] = undefined
      }
      if (script.parentNode) script.parentNode.removeChild(script)
    }
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      reject(new Error('JSONP request timed out'))
    }, timeoutMs)
    ;(window as any)[callbackName] = (data: T) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      cleanup()
      resolve(data)
    }
    const sep = url.includes('?') ? '&' : '?'
    script.src = `${url}${sep}callback=${callbackName}`
    script.async = true
    script.onerror = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      cleanup()
      reject(new Error('JSONP request failed'))
    }
    document.body.appendChild(script)
  })
}

// Partner responses arrive under any of five shapes.
function readResults(data: any): any[] {
  const list =
    data?.results ||
    data?.schools ||
    data?.data ||
    data?.result ||
    (Array.isArray(data) ? data : [])
  return Array.isArray(list) ? list : []
}

export interface MatchSchool {
  mBrandName?: string
  brandName?: string
  name?: string
  schoolName?: string
  mBrandID?: string
  brandID?: string
  mRecordID?: string
  recordID?: string
  id?: string
  logoURL?: string
  logoUrl?: string
  logo?: string
  programName?: string
  mProgramName?: string
  aboutBrand?: string
  headContent?: string
  bodyContent?: string
  accreditation?: string
  schoolHighlight?: string
  tcpaDisclaimer?: string
  clickURL?: string
  clickUrl?: string
}

export interface TcpaSearchResult {
  mRecordID?: string
  mBrandID?: string
  mBrandName?: string
  tcpaDisclaimer?: string
  disclaimer?: string
}

export function schoolName(school: MatchSchool): string {
  return (
    school.mBrandName ||
    school.brandName ||
    school.name ||
    school.schoolName ||
    ''
  )
}

export function schoolLogo(school: MatchSchool): string {
  return school.logoURL || school.logoUrl || school.logo || ''
}

export function schoolRecordId(school: MatchSchool): string {
  return (
    school.mRecordID ||
    school.recordID ||
    school.mBrandID ||
    school.brandID ||
    school.id ||
    ''
  )
}

/** The answers the form collects, in the form's own vocabulary. */
export interface LeadAnswers {
  degreeInterest: number
  areaStudy: number
  mProgram: number
  highestEducation: string
  gradYear: string
  startTimeframe: string
  militaryAffiliation: string
  zipCode: string
  city: string
  state: string
  address: string
  firstName: string
  lastName: string
  email: string
  phone: string
}

/** Everything that identifies this page and session rather than the person. */
export interface LeadContext {
  /** location.search of the landing URL — forwarded verbatim. */
  search: string
  /** One UUID per visit, reused on every call. */
  sessionId: string
  /** Used when the URL carries no publisher params. */
  defaultPubGroupID: string
}

function applyCertification(params: URLSearchParams): void {
  const { leadIdToken, trustedFormUrl, trustedFormPingUrl } =
    readCertificationFields()
  // A generated id keeps analytics and event dedupe working when the LeadiD
  // script is blocked, but carries no certification value — hence the log.
  params.set('leadIdToken', leadIdToken || generateUUID())
  if (trustedFormUrl) params.set('trustedFormUrl', trustedFormUrl)
  if (trustedFormPingUrl) params.set('trustedFormPingUrl', trustedFormPingUrl)
  console.log('[Consent Certification]', {
    jornaya: Boolean(leadIdToken),
    trustedFormCert: trustedFormUrl || '(none issued)',
    trustedFormPing: trustedFormPingUrl || '(none issued)',
  })
}

function baseParams(
  answers: LeadAnswers,
  ctx: LeadContext,
  mediaType: '1' | '2',
  includeContact: boolean,
): URLSearchParams {
  const urlParams = new URLSearchParams(ctx.search)
  const params = new URLSearchParams({
    degreeInterest: String(answers.degreeInterest),
    areaStudy: String(answers.areaStudy),
    mProgram: String(answers.mProgram),
    highestEducation: String(educationToApiValue(answers.highestEducation)),
    gradYear: answers.gradYear,
    militaryAffiliation: militaryAffiliationToApiValue(
      answers.militaryAffiliation,
    ),
    startTimeframe: String(startTimeframeToApiValue(answers.startTimeframe)),
    sub1: urlParams.get('sub1') || '',
    sub2: urlParams.get('sub2') || '',
    sub3: resolveSub3(ctx.search),
    zipCode: answers.zipCode,
    address: answers.address,
    city: answers.city,
    state: toStateAbbr(answers.state),
    webLandingURL: getWebLandingURL(),
    campusType: urlParams.get('campusType') || '1',
    mediaType: urlParams.get('mediaType') || mediaType,
    mSessionID: ctx.sessionId,
  })
  if (includeContact) {
    if (answers.firstName.trim()) params.set('firstName', answers.firstName.trim())
    if (answers.lastName.trim()) params.set('lastName', answers.lastName.trim())
    if (answers.email.trim()) params.set('email', answers.email.trim())
    const phone = answers.phone.replace(/\D/g, '')
    if (phone) params.set('phone', phone)
  }
  applyCertification(params)
  // Anything else the traffic source put on the landing URL rides along, but
  // never overwrites an answer the visitor actually gave.
  applyUrlPassthrough(params, ctx.search)
  Object.entries(
    getPublisherParams(ctx.search, { mPubGroupID: ctx.defaultPubGroupID }),
  ).forEach(([key, value]) => params.set(key, value))
  return params
}

function postParams(
  answers: LeadAnswers,
  ctx: LeadContext,
  recordID: string,
): URLSearchParams {
  const urlParams = new URLSearchParams(ctx.search)
  const { fbp, fbc } = getMetaCookies()
  const params = new URLSearchParams({
    mRecordID: recordID,
    firstName: answers.firstName,
    lastName: answers.lastName,
    email: answers.email,
    phone: answers.phone.replace(/\D/g, ''),
    sub1: urlParams.get('sub1') || '',
    sub2: urlParams.get('sub2') || '',
    sub3: resolveSub3(ctx.search),
    webLandingURL: getWebLandingURL(),
    mSessionID: ctx.sessionId,
  })
  if (fbp) params.set('fbp', fbp)
  if (fbc) params.set('fbc', fbc)
  applyCertification(params)
  applyUrlPassthrough(params, ctx.search)
  Object.entries(
    getPublisherParams(ctx.search, { mPubGroupID: ctx.defaultPubGroupID }),
  ).forEach(([key, value]) => params.set(key, value))
  return params
}

async function getJson<T>(url: string, label: string): Promise<T | null> {
  console.log(`[${label}] PING (GET):`, url)
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 8000)
  try {
    const response = await fetch(url, { signal: controller.signal })
    clearTimeout(timeout)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = (await response.json()) as T
    console.log(`[${label}] response (fetch):`, data)
    return data
  } catch (fetchErr) {
    clearTimeout(timeout)
    console.warn(`[${label}] fetch failed, falling back to JSONP:`, fetchErr)
    try {
      const data = await jsonpRequest<T>(url, 8000)
      console.log(`[${label}] response (jsonp):`, data)
      return data
    } catch (jsonpErr) {
      console.error(`[${label}] both fetch and JSONP failed:`, jsonpErr)
      return null
    }
  }
}

// Posts fire three ways in parallel: a keepalive fetch survives navigation, an
// <img> pixel stays alive long enough to complete, and a JSONP script tag works
// when the other two are blocked. Whichever lands first reports success.
function firePost(url: string, label: string, onConfirmed?: () => void): void {
  console.log(`[${label}] PING (GET):`, url)
  let confirmed = false
  const confirm = () => {
    if (confirmed) return
    confirmed = true
    onConfirmed?.()
  }
  try {
    fetch(url, { method: 'GET', mode: 'no-cors', keepalive: true })
      .then(() => confirm())
      .catch((err) => console.warn(`[${label}] fetch error:`, err))
  } catch (err) {
    console.warn(`[${label}] fetch threw:`, err)
  }
  try {
    const pixel = new Image()
    pixel.style.display = 'none'
    pixel.onload = () => confirm()
    pixel.onerror = () =>
      console.warn(`[${label}] pixel onerror (request may still have landed)`)
    pixel.src = url
    document.body.appendChild(pixel)
  } catch (err) {
    console.warn(`[${label}] pixel error:`, err)
  }
  try {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.onload = () => {
      confirm()
      script.parentNode?.removeChild(script)
    }
    script.onerror = () => script.parentNode?.removeChild(script)
    document.body.appendChild(script)
  } catch (err) {
    console.warn(`[${label}] jsonp error:`, err)
  }
}

// ---------------------------------------------------------------------------
// The five calls
// ---------------------------------------------------------------------------

export async function tcpaSearch(
  answers: LeadAnswers,
  ctx: LeadContext,
): Promise<TcpaSearchResult | null> {
  const params = baseParams(answers, ctx, '2', true)
  const url = `https://tcpa.media-matchers.com/search?${pruneEmptyParams(params).toString()}`
  return getJson<TcpaSearchResult>(url, 'TCPA Search API')
}

export function tcpaPost(
  answers: LeadAnswers,
  ctx: LeadContext,
  recordID: string,
  onConfirmed?: () => void,
): void {
  if (!recordID) {
    console.warn('[TCPA Post API] skipped — no mRecordID')
    return
  }
  const params = postParams(answers, ctx, recordID)
  firePost(
    `https://tcpa.media-matchers.com/post?${pruneEmptyParams(params).toString()}`,
    'TCPA Post API',
    onConfirmed,
  )
}

export async function leadsSearch(
  answers: LeadAnswers,
  ctx: LeadContext,
): Promise<MatchSchool[]> {
  const params = baseParams(answers, ctx, '1', true)
  const url = `https://leads.media-matchers.com/search?${pruneEmptyParams(params).toString()}`
  const data = await getJson<any>(url, 'Leads Search API')
  // Entries with no brand name render as blank "Partner School" cards.
  return readResults(data).filter((school) => schoolName(school))
}

export async function clicksSearch(
  answers: LeadAnswers,
  ctx: LeadContext,
): Promise<MatchSchool[]> {
  const params = baseParams(answers, ctx, '2', true)
  const url = `https://api.media-matchers.com/search?${pruneEmptyParams(params).toString()}`
  const data = await getJson<any>(url, 'Clicks Search API')
  return readResults(data).filter((school) => schoolName(school))
}

export function leadPost(
  answers: LeadAnswers,
  ctx: LeadContext,
  school: MatchSchool,
  onConfirmed?: () => void,
): void {
  const recordID = schoolRecordId(school)
  if (!recordID) {
    console.warn('[Leads Post API] skipped — no mRecordID on school')
    return
  }
  const params = postParams(answers, ctx, recordID)
  firePost(
    `https://leads.media-matchers.com/post?${pruneEmptyParams(params).toString()}`,
    'Leads Post API',
    onConfirmed,
  )
}
